import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { PURCHASED_BOOKS_APPS } from "../../graphql/queries";
import { ProductCard } from "../../components/ProductCard";
import { AppCard } from "../../components/AppCard";
import { useStore } from "../../Store";

export const MyBooksPage = ({ loadingCallback, productType = "Book" }) => {
  const user = useStore((state) => state.user);
  const [viewType, setViewType] = useState("grid");
  const [books, setBooks] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");

  const [getPurchasedBooksAndApps] = useLazyQuery(PURCHASED_BOOKS_APPS, {
    onCompleted: (data) => {
      setLoading(false)
      loadingCallback(false, "");
      setBooks(data.userPurchasedBooksAndApps.books || []);
      setApps(data.userPurchasedBooksAndApps.apps || []);
    },
    onError: () => {
      loadingCallback(false, "");
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (user.id) {
      setLoading(true)
      loadingCallback(true, `Fetching your ${productType === "Book" ? "books" : "courses"}.`);
      getPurchasedBooksAndApps({
        variables: {
          where: {
            userId: user.id,
          },
        },
      });
    }
  }, [getPurchasedBooksAndApps, loadingCallback, user, productType]);

  const handleFilterChange = (_, newFilter) => {
    if (newFilter) setFilter(newFilter);
  };

  const showBooks = filter === "All" || filter === "Books";
  const showApps = filter === "All" || filter === "Apps";

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          textAlign: "center",
          mb: 1,
        }}
      >
        Books, Courses, and Apps
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: "center",
          color: "text.secondary",
          mb: 4,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        Discover a world of knowledge with our curated collection of educational
        resources designed to enhance your learning journey.
      </Typography>

      {/* Filter Buttons */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          sx={{
            fontWeight: 500,
            fontSize: "16px",
            borderRadius: "8px",
            px: "18px",
            py: "6px",
            transition: "all 0.2s ease",
          }}
        >
          <ToggleButton value="All" sx={{p:"20px", background: "#fff", border: "none", textTransform: "capitalize"}}>All</ToggleButton>
          <ToggleButton value="Books" sx={{p:"20px", background: "#fff", border: "none", textTransform: "capitalize"}}>Books</ToggleButton>
          <ToggleButton value="Courses" sx={{p:"20px", background: "#fff", border: "none", textTransform: "capitalize"}}>Courses</ToggleButton>
          <ToggleButton value="Apps" sx={{p:"20px", background: "#fff", border: "none", textTransform: "capitalize"}}>Apps</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1.5rem",
        }}
      >
        {showApps &&
          apps.map((app) => (
            <AppCard key={app.id} app={app} viewType={viewType} />
          ))}
        {showBooks &&
          books.map((book) => (
            <ProductCard key={book.id} product={book} viewType={viewType} />
          ))}
      </Box>

      {/* Empty State */}
      {!loading ? apps.length === 0 && books.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography
            sx={{ fontSize: 18, fontWeight: 500 }}
            color="text.secondary"
            gutterBottom
          >
            No purchase found.
          </Typography>
        </Box>
      ) : <></>}
    </Box>
  );
};
